import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AccessRequestPanel from "../components/AccessRequestPanel";
import SearchTab from "../components/SearchTab";
import {
    apiFetch,
    clearStoredAuth,
    consumeReturnPath,
    getDiscordAuthorizeUrl,
    getStoredProfile,
    getStoredToken,
    persistAuthPayload,
    storeProfile,
} from "../func/auth";
import { REQUIRE_AUTH } from "../func/constants";
import { getDiscordAvatarUrl } from "../func/utils";

function BrandMark({ compact = false }) {
    return (
        <div>
            <div className={`${compact ? "text-3xl" : "text-4xl"} font-bold tracking-normal`}>
                <span className="text-pink-500">Meow</span>
                <span className="text-indigo-600 dark:text-indigo-400">Search</span>
            </div>
            {!compact && (
                <div className="mt-1 text-xs font-medium uppercase text-slate-400">
                    Discord archive
                </div>
            )}
        </div>
    );
}

function SignInPage({ authError, authLoading, onSignIn }) {
    return (
        <div className="min-h-dvh overflow-x-hidden bg-slate-50 px-4 py-8 text-slate-900 dark:bg-slate-900 dark:text-white sm:px-6">
            <main className="mx-auto grid min-h-[calc(100dvh-4rem)] w-full max-w-md place-items-center">
                <section className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/30 sm:p-8">
                        <div className="mb-10">
                            <BrandMark />
                        </div>
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h1 className="text-2xl font-bold tracking-normal text-slate-900 dark:text-white">
                                    Sign in
                                </h1>
                                <p className="text-sm leading-6 text-slate-500 dark:text-slate-400">
                                    Use Discord to continue to MeowSearch.
                                </p>
                            </div>

                            {authError && (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">
                                    {authError}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={onSignIn}
                                disabled={authLoading}
                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#4752C4] disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                <span className="grid h-5 w-5 place-items-center rounded bg-white/15 text-xs">D</span>
                                {authLoading ? "Signing in..." : "Sign in with Discord"}
                            </button>
                        </div>
                </section>
            </main>
        </div>
    );
}

function LoadingPage() {
    return (
        <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 dark:bg-slate-900">
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                Loading session...
            </div>
        </div>
    );
}

function SearchExperience({ apiToken, profile, onLogout }) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const user = profile?.user;

    return (
        <div className="flex h-dvh bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-white">
            <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="fixed right-4 top-4 z-50 grid h-10 w-10 place-items-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 lg:hidden"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    {isMobileMenuOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                </svg>
            </button>

            <aside
                className={`${
                    isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                } fixed z-40 flex h-full w-72 flex-col border-r border-slate-200 bg-white shadow-xl shadow-slate-200/60 transition-transform duration-300 ease-out dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/30 lg:relative lg:translate-x-0`}
            >
                <div className="border-b border-slate-200 p-5 dark:border-slate-700">
                    <BrandMark compact />
                </div>

                <nav className="flex-1 p-3">
                    <Link
                        to="/"
                        className="block rounded-lg bg-indigo-500 px-3 py-2.5 text-sm font-semibold text-white dark:bg-indigo-500 dark:text-white"
                    >
                        Search
                    </Link>
                    {profile?.is_admin && (
                        <Link
                            to="/admin"
                            className="mt-2 block rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-indigo-400"
                        >
                            Admin
                        </Link>
                    )}
                </nav>

                <div className="border-t border-slate-200 p-4 dark:border-slate-700">
                    {user && (
                        <div className="mb-4 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-700">
                            <img
                                src={getDiscordAvatarUrl(user)}
                                alt=""
                                className="h-10 w-10 rounded-lg bg-slate-200 object-cover dark:bg-slate-700"
                                onError={(event) => {
                                    event.currentTarget.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                                }}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-bold text-slate-900 dark:text-white">
                                    {user.global_name || user.username}
                                </div>
                                <button
                                    type="button"
                                    onClick={onLogout}
                                    className="text-xs font-medium text-slate-500 transition hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
                                >
                                    Sign out
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="text-center text-xs font-medium text-slate-400">.moe on top</div>
                </div>
            </aside>

            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/40 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            <main className="min-w-0 flex-1 overflow-hidden">
                <div className="h-full overflow-hidden p-4 sm:p-6">
                    <SearchTab apiToken={apiToken} />
                </div>
            </main>
        </div>
    );
}

function MainPage() {
    const [apiToken, setApiToken] = useState(() => getStoredToken());
    const [profile, setProfile] = useState(() => getStoredProfile());
    const [authError, setAuthError] = useState("");
    const [authLoading, setAuthLoading] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(REQUIRE_AUTH && Boolean(getStoredToken()) && !getStoredProfile());
    const [oauthCodeHandled, setOauthCodeHandled] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        if (!code || oauthCodeHandled) return;

        async function completeDiscordLogin() {
            setOauthCodeHandled(true);
            setAuthLoading(true);
            setSessionLoading(true);
            setAuthError("");
            try {
                const payload = await apiFetch(`/api/auth/discord/get_oauth?code=${encodeURIComponent(code)}`);
                const nextProfile = persistAuthPayload(payload);
                setApiToken(payload.token.access_token);
                setProfile(nextProfile);
                window.history.replaceState({}, document.title, window.location.pathname);

                const returnPath = consumeReturnPath();
                if (returnPath && returnPath !== window.location.pathname) {
                    window.location.assign(returnPath);
                }
            } catch (error) {
                setAuthError(error.message);
            } finally {
                setAuthLoading(false);
                setSessionLoading(false);
            }
        }

        completeDiscordLogin();
    }, [oauthCodeHandled]);

    useEffect(() => {
        if (!REQUIRE_AUTH) return;

        const params = new URLSearchParams(window.location.search);
        if (!apiToken || params.get("code")) return;

        let cancelled = false;
        async function refreshSession() {
            setSessionLoading(true);
            setAuthError("");
            try {
                const payload = await apiFetch("/api/auth/me", { token: apiToken });
                const tokenType = payload.token_type || getStoredProfile()?.token_type || null;
                const requiresReauthentication = Boolean(
                    payload.authorized && tokenType !== "discord"
                );
                const nextProfile = {
                    authorized: Boolean(payload.authorized) && !requiresReauthentication,
                    is_admin: Boolean(payload.is_admin),
                    user: payload.user || null,
                    request: payload.request || null,
                    token_type: tokenType,
                    requires_reauthentication: requiresReauthentication,
                };
                storeProfile(nextProfile);
                if (!cancelled) {
                    setProfile(nextProfile);
                    if (requiresReauthentication) {
                        const url = getDiscordAuthorizeUrl();
                        if (url) {
                            window.location.replace(url);
                            return;
                        }
                        setAuthError("Discord login is not configured.");
                    }
                }
            } catch (error) {
                clearStoredAuth();
                if (!cancelled) {
                    setApiToken(null);
                    setProfile(null);
                    setAuthError(error.message);
                }
            } finally {
                if (!cancelled) setSessionLoading(false);
            }
        }

        refreshSession();
        return () => {
            cancelled = true;
        };
    }, [apiToken]);

    function startDiscordLogin() {
        const url = getDiscordAuthorizeUrl();
        if (!url) {
            setAuthError("Discord login is not configured.");
            return;
        }

        window.location.href = url;
    }

    function logout() {
        clearStoredAuth();
        setApiToken(null);
        setProfile(null);
    }

    if (authLoading || sessionLoading) {
        return <LoadingPage />;
    }

    if (!REQUIRE_AUTH) {
        return <SearchExperience apiToken={apiToken} profile={profile} onLogout={logout} />;
    }

    if (!apiToken || !profile) {
        return (
            <SignInPage
                authError={authError}
                authLoading={authLoading}
                onSignIn={startDiscordLogin}
            />
        );
    }

    if (!profile.authorized) {
        return (
            <AccessRequestPanel
                apiToken={apiToken}
                profile={profile}
                onProfileChange={setProfile}
                onSignInAgain={startDiscordLogin}
                onLogout={logout}
            />
        );
    }

    return <SearchExperience apiToken={apiToken} profile={profile} onLogout={logout} />;
}

export default MainPage;
